// @ts-check
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import _ from 'lodash';
import ModelComponent from './ModelComponent';
import './ModelsContainer.scss';
import { useTranslation } from 'localization/useTranslation';
import { FormGroup, FormHelperText, HelperText, HelperTextItem } from '@patternfly/react-core';
import { ModelPropType } from './ModelPropTypes';

/**
 *
 * @param {Model} model
 * @param {Record<string, any>} data
 * @returns {any}
 */
const modelValue = (model, data) => _.get(data, model.path);

/**
 *
 * @param {Model} model
 * @param {Record<string, any>} data
 * @param {(model: Model, value: any) => void} updateData
 * @param {*} T
 * @param {boolean} isLoading
 * @returns {JSX.Element}
 */
const renderModel = (model, data, updateData, T, isLoading) => {

  const groupValue = modelValue(model, data);
  const groupHasError = modelFormFunc(model.hasError, model, groupValue, false, data);
  const groupErrorText = modelFormFunc(model.errorText, model, groupValue, null, data);

  const validation = () => {
    if (groupHasError) {
      return { validateStatus: 'error', helperTextInvalid: groupErrorText };
    }
    // if (groupHasWarnings) {
    //   return { validateStatus: 'warning', helperTextInvalid: groupWarningText };
    // }
    // if (showSuccess) {
    //   return { validateStatus: 'success', helperTextInvalid: '' };
    // }
    return { validateStatus: 'default', helperTextInvalid: '' };
  };

  const { validateStatus } = validation();
  const allModels = _.map(model.group ?? [model], model => { // Render as either a group (if group attribute) or plain model
    const value = modelValue(model, data);
    const hasError = modelFormFunc(model.hasError, model, value, false, data);
    return (<ModelComponent
      key={model.path}
      model={model}
      hasError={hasError}
      onChange={updateData}
      value={value}
      isLoading={isLoading} />);
  });

  if (model.noTitle) {
    return (<>
      { allModels }
    </>);
  }
  return (<FormGroup
    label={model.disableTitle ? '' : T.localize(model.label)}
    isRequired={model.isRequired}
    fieldId={model.path}>
    { validateStatus === 'error' && model.helpText && (
      <FormHelperText>
        <HelperText>
          <HelperTextItem variant="error">
            { model.helpText }
          </HelperTextItem>
        </HelperText>
      </FormHelperText>
    ) }
    { allModels }
  </FormGroup>
  );
};

const modelFormFunc = (prop, model, value, defaultValue, data) => {
  if (prop == null) {
    return defaultValue;
  }

  if (_.isFunction(prop)) {
    return prop(value, model, data);
  }

  return prop;
};


// Standard usage:
//
// <ModelsContainer models={models} data={data} setData={setData} />
//
// Note: Only for special cases when the standard rendering in column is not needed:
//  <ModelsContainer ...>
//  {
//     (renderModel) => {
//       return (
//         <div>
//           <div>{ renderModel('some.path.in.models.array') }</div>
//           <div>{ renderModel('someother.property') }</div>
//         </div>
//       )
//     }
//  }
//  </ModelsContainer>
//

/**
 * @typedef {object} ModelsContainerProps
 * @property {Model[]} models
 * @property {Record<string, any>} data
 * @property {(newData: Record<string, any>, model: Model) => void} setData
 * @property {boolean} [isLoading]
 * @property {*} [children] Used as render property. func (renderFunc, models). Use the renderFunc(string) as renderer
 */

/**
 *
 * @param {ModelsContainerProps} props
 * @returns {JSX.Element}
 */
const ModelsContainer = ({ models, data, setData, isLoading, children }) => {
  const T = useTranslation();
  const updateData = useCallback((model, value) => {
    const newData = { ...data };
    _.set(newData, model.path, value);
    setData(newData, model);
  }, [data, setData]);
  const components = useMemo(() => {
    return _.reduce(models, (result, model) => {
      result[model.path] = renderModel(model, data, updateData, T, isLoading);
      return result;
    }, {});
  }, [models, data, updateData, T, isLoading]);

  const _renderModel = useCallback((name) => {
    return components[name];
  }, [components]);

  const content = useMemo(() => {
    if (_.isFunction(children)) {
      return children(_renderModel);
    }
    return _.map(models, (model, index) => {
      const key = 'content-' + index + '-' + model.path;
      return (
        <div className="model-container--input" key={key}>
          { _renderModel(model.path) }
        </div>
      );
    });

  }, [_renderModel, children, models]);
  return (
    <div className="model-container">
      { content }
    </div>
  );
};

ModelsContainer.propTypes = {
  models: PropTypes.arrayOf(ModelPropType),
  data: PropTypes.object,
  setData: PropTypes.func.isRequired,
  children: PropTypes.func,
  isLoading: PropTypes.bool
};

export default React.memo(ModelsContainer);
